import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { Sparkline } from '../components/Sparkline';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { 
  Truck, 
  Fuel, 
  Users,
  TrendingUp,
  Plus,
  ArrowRight,
  AlertTriangle,
  ChevronRight,
  X,
  FileWarning,
  Wrench,
  ShieldAlert,
  CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import AddCostDialog from '../components/AddCostDialog';

const COST_LEGEND_FORMATTER = (value) => (
  <span className="text-slate-600 text-xs uppercase tracking-wider">{value}</span>
);

const COLORS = ['#0F172A', '#2563EB', '#059669', '#D97706', '#9333EA', '#DB2777'];

// Chart colors for cost categories
const CHART_COLORS = {
  fuel: '#2563EB',      // Blue
  service: '#0F172A',   // Slate-950
  other: '#059669',     // Emerald
  tyres: '#D97706',     // Amber
  insurance: '#9333EA', // Purple
  maintenance: '#DB2777', // Pink
  labour: '#0D9488',    // Teal
};

// Shared chart config constants
const AXIS_TICK = { fill: '#64748B', fontSize: 11, fontFamily: 'IBM Plex Mono, monospace' };
const TOOLTIP_STYLE = { backgroundColor: '#fff', border: '1px solid #0F172A', borderRadius: '2px', fontFamily: 'IBM Plex Sans, sans-serif', fontSize: '12px' };
const BAR_RADIUS_NONE = [0, 0, 0, 0];
const BAR_RADIUS_TOP = [0, 0, 0, 0];

// Format Rand: R 1,234.56
const fmtR = (v) => {
  if (v == null || isNaN(v)) return 'R 0.00';
  const n = Number(v);
  const parts = n.toFixed(2).split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `R ${parts.join('.')}`;
};

const StatCard = ({ title, value, subValue, icon: Icon, accent = false }) => {
  return (
    <div
      className={`relative h-full border border-slate-200 bg-white p-6 transition-colors hover:border-slate-400 ${
        accent ? 'bg-slate-950 text-white border-slate-950' : ''
      }`}
    >
      <div className="flex items-start justify-between mb-3">
        <p className={`text-[13px] font-extrabold uppercase tracking-[0.18em] ${accent ? 'text-slate-300' : 'text-slate-700'}`}>
          {title}
        </p>
        <Icon className={`w-5 h-5 ${accent ? 'text-slate-300' : 'text-slate-500'}`} strokeWidth={2} />
      </div>
      <p className={`font-display font-black text-4xl tracking-tight tabular-nums leading-none ${accent ? 'text-white' : 'text-slate-950'}`}>
        {value}
      </p>
      {subValue && (
        <p className={`text-sm mt-3 font-mono font-semibold tabular-nums ${accent ? 'text-slate-300' : 'text-slate-600'}`}>
          {subValue}
        </p>
      )}
    </div>
  );
};

const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);
  const [costDialogOpen, setCostDialogOpen] = useState(false);
  const [licenseModalOpen, setLicenseModalOpen] = useState(false);
  const [selectedLicenseAlert, setSelectedLicenseAlert] = useState(null);
  const [newLicenseDate, setNewLicenseDate] = useState('');
  const [savingLicense, setSavingLicense] = useState(false);
  const [loadingDemo, setLoadingDemo] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statsData, vehiclesData] = await Promise.all([
        api.getDashboardStats(period),
        api.getVehicles()
      ]);
      setStats(statsData);
      setVehicles(vehiclesData);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [period]);

  const handleLoadDemoFleet = useCallback(async () => {
    if (!window.confirm('Load a demo fleet with 4 vehicles, 3 drivers and 30 days of costs?\n\nYou can wipe it any time from the Vehicles page.')) {
      return;
    }
    setLoadingDemo(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const axios = (await import('axios')).default;
      const r = await axios.post(`${API_URL}/api/onboarding/load-demo-fleet`);
      toast.success(`✨ Demo fleet loaded — ${r.data.vehicles} vehicles & ${r.data.cost_entries} costs`);
      await fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load demo fleet');
    } finally {
      setLoadingDemo(false);
    }
  }, [fetchData]);

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [fetchData]);

  const handleDismissAlert = async (alertKey) => {
    try {
      await api.dismissAlert(alertKey);
      // Refresh stats to remove dismissed alert
      const statsData = await api.getDashboardStats(period);
      setStats(statsData);
    } catch (error) {
      toast.error('Failed to dismiss alert');
    }
  };

  const handleOpenLicenseModal = (alert) => {
    setSelectedLicenseAlert(alert);
    setNewLicenseDate('');
    setLicenseModalOpen(true);
  };

  const handleUpdateLicense = async () => {
    if (!newLicenseDate) {
      toast.error('Please enter the new license expiry date');
      return;
    }
    setSavingLicense(true);
    try {
      await api.updateVehicleLicense(selectedLicenseAlert.vehicle_id, newLicenseDate);
      toast.success('License date updated successfully');
      setLicenseModalOpen(false);
      setSelectedLicenseAlert(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update license date');
    } finally {
      setSavingLicense(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8 space-y-6">
        <div className="h-12 bg-slate-100 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border border-slate-200">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 bg-white border-r border-slate-200 last:border-r-0 p-5">
              <div className="ascii-loader font-mono text-slate-300 text-xs" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const pieData = stats?.cost_by_type?.filter(item => item.amount > 0) || [];

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 pb-4 border-b border-slate-200">
        <div>
          <p className="text-[13px] font-mono font-extrabold uppercase tracking-[0.2em] text-slate-700 mb-2">
            [ Mission Control ]
          </p>
          <h1 className="font-display text-5xl lg:text-6xl font-black text-slate-950 tracking-tight leading-none">
            Dashboard
          </h1>
          <p className="text-slate-600 mt-3 text-base font-medium">Fleet overview and key operating metrics.</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[140px] h-10" data-testid="period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="day">Today</SelectItem>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
            </SelectContent>
          </Select>
          <Button
            onClick={() => setCostDialogOpen(true)}
            className="h-10"
            data-testid="add-cost-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Cost
          </Button>
        </div>
      </div>

      {/* Alerts */}
      {stats?.alerts?.length > 0 && (
        <div className="space-y-2" data-testid="alerts-section">
          {stats.alerts.map((alert, i) => {
            const isExpired = alert.requires_new_date;
            const isError = alert.severity === 'error';
            const alertIcon = alert.type === 'service' ? Wrench
              : alert.type === 'license' || alert.type === 'driver_license' ? FileWarning
              : alert.type === 'fuel_efficiency' ? Fuel
              : ShieldAlert;
            const AlertIcon = alertIcon;

            return (
              <div
                key={alert.alert_key || `alert-${alert.type}-${i}`}
                data-testid={`alert-${alert.type}-${i}`}
                className={`flex items-start gap-3 rounded-sm px-4 py-3 border ${
                  isError
                    ? 'bg-rose-50 border-rose-200'
                    : 'bg-amber-50 border-amber-200'
                }`}
              >
                <AlertIcon className={`w-4 h-4 mt-0.5 shrink-0 ${isError ? 'text-rose-700' : 'text-amber-700'}`} />
                <span className={`flex-1 text-sm ${isError ? 'text-rose-900' : 'text-amber-900'}`}>
                  {alert.message}
                </span>
                {isExpired && alert.vehicle_id && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="shrink-0 h-7 text-[11px] uppercase tracking-wider border-rose-300 text-rose-700 hover:bg-rose-100"
                    onClick={() => handleOpenLicenseModal(alert)}
                    data-testid={`update-license-btn-${i}`}
                  >
                    Update License
                  </Button>
                )}
                {!isExpired && alert.dismissable && (
                  <button
                    className={`shrink-0 rounded-none p-0.5 hover:bg-black/10 ${isError ? 'text-rose-500' : 'text-amber-500'}`}
                    onClick={() => handleDismissAlert(alert.alert_key)}
                    data-testid={`dismiss-alert-${i}`}
                    title="Dismiss alert"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Stats Grid - Control Room */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border border-slate-200">
        <div className="border-r border-b lg:border-b-0 border-slate-200">
          <StatCard
            title="Total Cost"
            value={fmtR(stats?.total_cost)}
            subValue={`${stats?.entry_count || 0} entries`}
            icon={TrendingUp}
            accent={true}
          />
        </div>
        <div className="border-r border-b lg:border-b-0 border-slate-200">
          <StatCard
            title="Fuel Cost"
            value={fmtR(stats?.fuel_cost)}
            subValue={`${(stats?.total_liters || 0).toFixed(0)} L`}
            icon={Fuel}
          />
        </div>
        <div className="border-r border-b md:border-b-0 border-slate-200">
          <StatCard
            title="Vehicles"
            value={stats?.total_vehicles || 0}
            subValue={`${stats?.active_vehicles || 0} active`}
            icon={Truck}
          />
        </div>
        <div>
          <StatCard
            title="Drivers"
            value={stats?.total_drivers || 0}
            subValue={`${stats?.active_drivers || 0} active`}
            icon={Users}
          />
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Monthly Costs Chart */}
        <Card className="lg:col-span-3 rounded-none">
          <CardHeader className="pb-3 border-b border-slate-200">
            <p className="text-[12px] font-mono font-bold uppercase tracking-[0.2em] text-slate-600">Time Series</p>
            <CardTitle className="font-display text-2xl font-black text-slate-950 tracking-tight">
              Cost Trend
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {stats?.monthly_costs?.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={stats.monthly_costs} barSize={32}>
                  <XAxis 
                    dataKey="month" 
                    axisLine={false}
                    tickLine={false}
                    tick={AXIS_TICK}
                  />
                  <YAxis 
                    axisLine={false}
                    tickLine={false}
                    tick={AXIS_TICK}
                    tickFormatter={(value) => `R${value}`}
                  />
                  <Tooltip 
                    formatter={(value) => [fmtR(value), '']}
                    contentStyle={TOOLTIP_STYLE}
                    cursor={{ fill: 'rgba(15, 23, 42, 0.04)' }}
                  />
                  <Bar dataKey="fuel" stackId="a" fill={CHART_COLORS.fuel} name="Fuel" radius={BAR_RADIUS_NONE} />
                  <Bar dataKey="service" stackId="a" fill={CHART_COLORS.service} name="Service" />
                  <Bar dataKey="labour" stackId="a" fill={CHART_COLORS.labour} name="Labour" />
                  <Bar dataKey="other" stackId="a" fill={CHART_COLORS.other} name="Other" radius={BAR_RADIUS_TOP} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-slate-400 font-mono text-xs uppercase tracking-wider">
                — No cost data —
              </div>
            )}
          </CardContent>
        </Card>

        {/* Cost Distribution */}
        <Card className="lg:col-span-2 rounded-none">
          <CardHeader className="pb-3 border-b border-slate-200">
            <p className="text-[12px] font-mono font-bold uppercase tracking-[0.2em] text-slate-600">Distribution</p>
            <CardTitle className="font-display text-2xl font-black text-slate-950 tracking-tight">
              Cost Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="amount"
                    nameKey="type"
                  >
                    {pieData.map((entry) => (
                      <Cell key={`cell-${entry.type}`} fill={COLORS[pieData.indexOf(entry) % COLORS.length]} stroke="#fff" strokeWidth={1.5} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => fmtR(value)}
                    contentStyle={TOOLTIP_STYLE}
                  />
                  <Legend 
                    verticalAlign="bottom"
                    iconType="square"
                    iconSize={8}
                    formatter={COST_LEGEND_FORMATTER}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-slate-400 font-mono text-xs uppercase tracking-wider">
                — No data —
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Vehicle Summary Table */}
      <Card className="rounded-none">
        <CardHeader className="flex flex-row items-end justify-between pb-3 border-b border-slate-200">
          <div>
            <p className="text-[12px] font-mono font-bold uppercase tracking-[0.2em] text-slate-600">Inventory</p>
            <CardTitle className="font-display text-2xl font-black text-slate-950 tracking-tight">
              Fleet Overview
            </CardTitle>
          </div>
          <Link to="/vehicles">
            <Button variant="ghost" size="sm" data-testid="view-all-vehicles">
              View All <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent className="p-0">
          {stats?.vehicle_summaries?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full data-table">
                <thead>
                  <tr>
                    <th>Vehicle</th>
                    <th>Registration</th>
                    <th>Status</th>
                    <th className="text-right">Avg km/L · L/hr</th>
                    <th className="text-right">Latest km/L · L/hr</th>
                    <th className="text-right">Trend</th>
                    <th className="text-right">Fuel Cost</th>
                    <th className="text-right">Total Cost</th>
                    <th aria-label="actions"></th>
                  </tr>
                </thead>
                <tbody>
                  {stats.vehicle_summaries.map((vehicle) => (
                    <tr key={vehicle.id} className={vehicle.has_efficiency_issue ? 'bg-rose-50' : ''}>
                      <td>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-slate-900">{vehicle.name}</span>
                          {vehicle.has_efficiency_issue && (
                            <AlertTriangle className="w-4 h-4 text-rose-600" title="Fuel efficiency issue detected" />
                          )}
                          {vehicle.include_in_fleet_avg === false && (
                            <span
                              className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-600 border border-slate-200"
                              title="Excluded from fleet average — meter reading not tracked"
                              data-testid="no-meter-tracking-badge"
                            >
                              no meter tracking
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className="font-data text-slate-700">{vehicle.registration}</span>
                      </td>
                      <td>
                        <span className={`alert-badge ${vehicle.status === 'active' ? 'alert-badge-success' : vehicle.status === 'maintenance' ? 'alert-badge-warning' : 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                          {vehicle.status}
                        </span>
                      </td>
                      <td className="text-right font-data">
                        {vehicle.meter_type === 'hours' ? (
                          vehicle.avg_l_per_hour != null ? (
                            <span
                              className={vehicle.avg_l_per_hour <= 2 ? 'text-emerald-700 font-bold' : vehicle.avg_l_per_hour <= 4 ? 'text-amber-700 font-bold' : 'text-rose-700 font-bold'}
                              title="Liters per operating hour (lower = better)"
                            >
                              {vehicle.avg_l_per_hour.toFixed(2)} L/hr
                            </span>
                          ) : <span className="text-slate-300">—</span>
                        ) : vehicle.avg_km_per_liter ? (
                          <span className={vehicle.avg_km_per_liter >= 10 ? 'text-emerald-700 font-bold' : vehicle.avg_km_per_liter >= 7 ? 'text-amber-700 font-bold' : 'text-rose-700 font-bold'}>
                            {vehicle.avg_km_per_liter}
                          </span>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className={`text-right font-data ${vehicle.has_efficiency_issue ? 'text-rose-700 font-bold' : 'text-slate-700'}`}>
                        {vehicle.meter_type === 'hours'
                          ? (vehicle.latest_l_per_hour != null
                              ? `${vehicle.latest_l_per_hour.toFixed(2)} L/hr`
                              : <span className="text-slate-300">—</span>)
                          : (vehicle.latest_km_per_liter
                              ? vehicle.latest_km_per_liter
                              : <span className="text-slate-300">—</span>)}
                      </td>
                      <td className="text-right">
                        <VehicleTrendSparkline vehicleId={vehicle.id} meterType={vehicle.meter_type} />
                      </td>
                      <td className="text-right font-data text-slate-700">
                        {fmtR(vehicle.fuel_cost)}
                      </td>
                      <td className="text-right font-data font-bold text-slate-950">
                        {fmtR(vehicle.total_cost)}
                      </td>
                      <td className="text-right">
                        <Link to={`/vehicles/${vehicle.id}`}>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <ChevronRight className="w-4 h-4 text-slate-400" />
                          </Button>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-slate-400">
              <Truck className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No vehicles added yet</p>
              <div className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link to="/vehicles">
                  <Button variant="outline" data-testid="add-first-vehicle">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Your First Vehicle
                  </Button>
                </Link>
                <Button
                  variant="default"
                  onClick={handleLoadDemoFleet}
                  disabled={loadingDemo}
                  data-testid="load-demo-fleet-btn"
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  {loadingDemo ? 'Loading…' : '✨ Load Demo Fleet'}
                </Button>
              </div>
              <p className="mt-3 text-xs text-slate-400">
                Try FleetCost.App with 4 sample vehicles, 3 drivers and a month of costs — you can wipe it any time from Vehicles.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Cost Dialog */}
      <AddCostDialog 
        open={costDialogOpen} 
        onOpenChange={setCostDialogOpen}
        vehicles={vehicles}
        onSuccess={fetchData}
      />

      {/* Update License Date Modal */}
      <Dialog open={licenseModalOpen} onOpenChange={setLicenseModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <FileWarning className="w-5 h-5 text-rose-600" />
              Update Vehicle License Date
            </DialogTitle>
            <DialogDescription>
              {selectedLicenseAlert?.vehicle_name
                ? `The license for "${selectedLicenseAlert.vehicle_name}" has expired. Please enter the new license expiry date to confirm renewal and clear this alert.`
                : 'Please enter the new license expiry date to confirm renewal.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label htmlFor="new_license_date">New License Expiry Date</Label>
              <Input
                id="new_license_date"
                type="date"
                value={newLicenseDate}
                onChange={(e) => setNewLicenseDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="font-data"
                data-testid="new-license-date-input"
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setLicenseModalOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleUpdateLicense}
                disabled={savingLicense || !newLicenseDate}
                data-testid="confirm-license-update-btn"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                {savingLicense ? 'Saving...' : 'Confirm New License'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardPage;

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Lightweight sparkline that fetches the vehicle's last N efficiency segments
 * lazily (one request per row on mount). Cached in memory across renders via
 * a component-level state so the value doesn't refetch on parent re-renders.
 */
function VehicleTrendSparkline({ vehicleId, meterType }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/vehicles/${vehicleId}/efficiency-history?limit=12`);
        if (!cancelled) setData(r.data);
      } catch {
        if (!cancelled) setData({ points: [] });
      }
    })();
    return () => { cancelled = true; };
  }, [vehicleId]);
  const values = (data?.points || []).map((p) => p.value);
  return (
    <Sparkline
      values={values}
      unit={meterType === 'hours' ? 'L/hr' : 'km/L'}
      className="inline-block"
      testId={`sparkline-${vehicleId}`}
    />
  );
}
